import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SpinnerIcon } from '@phosphor-icons/react';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  message?: string;
  children?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  dangerous?: boolean;
  wide?: boolean; // For dialogs that need more width (e.g., tables)
  disabled?: boolean; // Additional disable condition for the confirm button
  className?: string; // Additional classes for AlertDialogContent (overrides wide)
  // When false, the confirm button is a regular button that does NOT auto-
  // close the dialog on click — callers control closing via `onClose` (e.g.
  // after an async mutation completes successfully). Defaults to true to
  // preserve existing AlertDialog behavior for current consumers.
  closeOnConfirm?: boolean;
}

const ConfirmDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  children,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  dangerous = false,
  wide = false,
  disabled = false,
  className,
  closeOnConfirm = true,
}) => {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => !nextOpen && onClose()}
    >
      <AlertDialogContent className={className ?? (wide ? 'max-w-2xl' : '')}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {message && (
            <AlertDialogDescription className="whitespace-pre-line">
              {message}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children && <div className="py-4">{children}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading} onClick={onClose}>
            {cancelText}
          </AlertDialogCancel>
          {closeOnConfirm ? (
            <AlertDialogAction
              onClick={onConfirm}
              disabled={loading || disabled}
              className={
                dangerous
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  : undefined
              }
            >
              {loading && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
              {confirmText}
            </AlertDialogAction>
          ) : (
            // Plain button (not AlertDialog.Action) so Radix doesn't auto-close
            // the dialog before the caller's async work finishes. Caller closes
            // via the `open` prop on success.
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading || disabled}
              className={
                'inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium text-white shadow disabled:pointer-events-none disabled:opacity-50 ' +
                (dangerous
                  ? 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600'
                  : 'bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900')
              }
            >
              {loading && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
              {confirmText}
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
